import { Token } from '@opallabs/lapidary'
import {
  type CalendarFilterCategory,
  type CalendarFilterMode,
  type CalendarFilterOption,
} from '../types'
import { type CalendarValueTree } from '../valueTree'
import { ModeGroupLabel } from './ModeSelect'
import * as styles from './styles'

/** The options behind the chosen ids, in the order they were chosen. */
export const resolveFilterOptions = (
  category: CalendarFilterCategory,
  valueIds: readonly string[]
): CalendarFilterOption[] =>
  valueIds
    .map((valueId) => category.options.find((option) => option.id === valueId))
    .filter((option): option is CalendarFilterOption => option !== undefined)

/**
 * A token's words: the row the value sits under, said quietly in front of it,
 * and the value itself.
 *
 * Nothing here says Include or Exclude. The heading over the group does, once
 * for every token under it, which is the only way a category holding a matched
 * set beside a hidden one can be read at a glance.
 *
 * A row chosen whole stands for everything under it, and the two kinds of row
 * say so differently. A channel is a value a person filters by, so it names
 * itself: "All Instagram". A label set is never a value of its own — its name
 * only ever qualifies a label, the way "Audience" qualifies "Detractors" — so
 * the set stays in front and the value is the whole of it.
 */
const wordsFor = (
  category: CalendarFilterCategory,
  option: CalendarFilterOption,
  tree: CalendarValueTree
): Readonly<{ label: string; prefix?: string }> => {
  if (!tree.childrenOf.has(option.id))
    return { label: option.label, prefix: rowAbove(category, option) }

  return tree.modeScope === 'parent'
    ? { label: 'All', prefix: option.label }
    : { label: `All ${option.label}` }
}

/**
 * The heading a value sits under, where that heading is a row of its own.
 *
 * A channel, a label set and a half of the status list head their group as well
 * as being in it, so they name no row above them: "Instagram Instagram" says
 * nothing. A heading nobody offers as a row is not a filter at all, and the
 * design leaves it off the token.
 */
const rowAbove = (
  category: CalendarFilterCategory,
  option: CalendarFilterOption
): string | undefined => {
  const { group } = option
  if (group === undefined || group.id === option.id) return undefined

  return category.options.some((each) => each.id === group.id)
    ? group.label
    : undefined
}

/**
 * The exceptions to one chosen row, in the words the row wears them in.
 *
 * A carve-out has no token of its own: it is an exception to the row above it,
 * and it is dropped by dropping that row. "Instagram except Acme US" reads the
 * same whether the category matches Instagram or hides it.
 */
const exceptionsTo = (
  option: CalendarFilterOption,
  carved: readonly CalendarFilterOption[]
): string | undefined => {
  const under = carved.filter((value) => value.group?.id === option.id)
  return under.length === 0
    ? undefined
    : `except ${under.map((value) => value.label).join(', ')}`
}

export type CategoryTokensProps = Readonly<{
  /** The values carved out of the chosen ones, wherever they belong. */
  carved: readonly CalendarFilterOption[]
  category: CalendarFilterCategory
  /** What this group of tokens does, said once over all of them. */
  mode: CalendarFilterMode
  onRemove: (valueId: string) => void
  options: readonly CalendarFilterOption[]
  renderOptionIcon?: (
    category: CalendarFilterCategory,
    option: CalendarFilterOption
  ) => React.ReactNode
  tree: CalendarValueTree
}>

/**
 * One group of chosen values under a category's row: what it matches, or what
 * it hides, under a heading that says which.
 *
 * A category that makes the choice once has one of these. Labels has two
 * whenever a person has matched one set and hidden another.
 */
export const CategoryTokens: React.FC<CategoryTokensProps> = ({
  carved,
  category,
  mode,
  onRemove,
  options,
  renderOptionIcon,
  tree,
}) => {
  if (options.length === 0) return null

  return (
    <div className={styles.selectors.tokenGroup}>
      <ModeGroupLabel mode={mode} />
      <ul className={styles.selectors.tokenList}>
        {options.map((option) => {
          const icon = renderOptionIcon?.(category, option)
          const { label, prefix } = wordsFor(category, option, tree)
          const exceptions = exceptionsTo(option, carved)

          return (
            <li key={option.id} data-value={option.id}>
              <Token
                leadingText={prefix}
                // A token carries a mark, never a colour: the design's tokens
                // wear no dot, and the set's name says what the row's dot did.
                leadingNode={
                  icon === undefined || icon === null ? undefined : (
                    <span aria-hidden={true}>{icon}</span>
                  )
                }
                onRemoveToken={() => onRemove(option.id)}
                removable={true}
                text={label}
                trailingText={exceptions}
                value={option.id}
              />
            </li>
          )
        })}
      </ul>
    </div>
  )
}
CategoryTokens.displayName = 'Calendar: CategoryTokens'
